/**
 * shared/types.ts
 * 前后端共享的 TypeScript 类型定义
 * 包含：枚举、API响应格式、实体模型、分页结构
 */

// ======================== 枚举定义 ========================

/** 用户角色 */
export enum Role {
  ADMIN = 'admin',
  AGENT = 'agent',
}

/** 审核状态 */
export enum AuditStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected',
}

/** 卡类型 */
export enum CardType {
  TUOTU = 'tuotu',
  TALKTIKO = 'talktiko',
  ESIM = 'esim',
}

/** 分账规则类型 */
export enum RuleType {
  PERCENTAGE = 'percentage',
  FIXED = 'fixed',
}

/** 代理等级（数值越小等级越高） */
export enum AgentLevel {
  /** 一级代理（最高等级） */
  LEVEL_1 = 1,
  /** 二级代理 */
  LEVEL_2 = 2,
  /** 三级代理（最低等级，新代理默认） */
  LEVEL_3 = 3,
}

/** 代理等级取值范围 */
export const AGENT_LEVELS: number[] = [AgentLevel.LEVEL_1, AgentLevel.LEVEL_2, AgentLevel.LEVEL_3];

/** 代理等级显示名 */
export const AGENT_LEVEL_LABELS: Record<number, string> = {
  [AgentLevel.LEVEL_1]: '一级代理',
  [AgentLevel.LEVEL_2]: '二级代理',
  [AgentLevel.LEVEL_3]: '三级代理',
};

/** 用户状态 */
export type UserStatus = 'active' | 'disabled';

/** 卡号状态 */
export type CardStatus = 'unbound' | 'bound';

/** 导入批次状态 */
export type BatchStatus = 'pending' | 'validated' | 'confirmed' | 'failed';

/** 批次类型 */
export type BatchType = 'purchase' | 'card_binding';

/** 规则状态 */
export type RuleStatus = 'active' | 'inactive';

// ======================== 证件类型 & 利润可见性 ========================

/**
 * 证件类型（2026-09-17 语义升级）：domestic=身份证 / international=护照。
 * 语义来源：由「录入时用户选择的证件类型」决定（real_name_records.identity_type）；
 * 不再与代理身份绑定（users.identity_type 已移除）。
 */
export type IdentityType = 'domestic' | 'international';

/** 证件类型显示名（2026-09-17 改造：身份证 / 护照） */
export const IDENTITY_TYPE_LABELS: Record<IdentityType, string> = {
  domestic: '身份证',
  international: '护照',
};

/**
 * 三级代理（agent_level=3）利润屏蔽字段黑名单（snake/camel 双形态）
 * 前后端同源维护：后端序列化层剥离 + 前端 store/字段层兜底过滤均引用此常量
 */
export const PROFIT_FIELDS_TO_MASK = [
  'commission_amount',       // 单条分账金额
  'commissionAmount',        // 驼峰别名
  'totalCommission',         // 汇总分账
  'total_commission',
  'rule_value',              // 规则比例/固定金额
  'rule_percentage',
  'profit_distribution',
  'order_commission',
  'expected_commission',
] as const;

/** 护照信息（国际客户录入，2026-09-08） */
export interface PassportInfo {
  passport_number: string;
  /** ISO alpha-2 优先（如 CN/US/JP）；下拉+自由文本 */
  nationality: string;
  /** 出生日期 YYYY-MM-DD */
  date_of_birth: string;
  /** 护照有效期至 YYYY-MM-DD，必须 >= 提交当天 */
  passport_expiry_date: string;
  /** 护照照片页路径（复用 id_card_front_path 存储位） */
  passport_front_path?: string;
}

// ======================== API 响应格式 ========================

/** 统一API响应格式 */
export interface ApiResponse<T = any> {
  /** 0=成功，非0=错误 */
  code: number;
  /** 成功时返回数据，失败时为null */
  data: T;
  /** 成功时为"success"，失败时为错误描述 */
  message: string;
}

/** 分页响应结构 */
export interface PaginatedResult<T> {
  list: T[];
  total: number;
  page: number;
  pageSize: number;
}

// ======================== 用户相关 ========================

/** 用户实体 */
export interface User {
  id: number;
  username: string;
  real_name: string;
  role: Role;
  phone?: string;
  card_segment?: string;
  /** 代理等级（1=一级最高，3=三级最低，新代理默认三级；管理员无此属性） */
  agent_level?: number;
  /** 上级代理 id（多级代理层级，2026-09-06；null=无上级） */
  parent_id?: number | null;
  /** 上级代理姓名（JOIN 补充） */
  parent_name?: string | null;
  /** 直接下级数量（子查询补充） */
  subordinate_count?: number;
  /**
   * 利润可见性（2026-09-08）：后端登录响应基于 agent_level 派生下发
   * admin / 一级 / 二级 = true；三级 = false；前端对 undefined 一律视为 false（保守拒绝）
   */
  profit_visible?: boolean;
  status: UserStatus;
  created_at: string;
}

/** 上级链节点（个人中心展示关系链用） */
export interface ParentChainNode {
  id: number;
  username: string;
  real_name: string;
  agent_level: number;
}

/** 当前代理个人资料（含完整上级链） */
export interface MyProfile {
  id: number;
  username: string;
  real_name: string;
  role: Role;
  phone?: string | null;
  card_segment?: string | null;
  agent_level: number | null;
  parent_id: number | null;
  parent_name: string | null;
  subordinate_count: number;
  status: UserStatus;
  /** 完整上级链（自顶级到当前代理） */
  parent_chain: ParentChainNode[];
  /** 当前代理是否有权限创建下级（1/2 级=true，三级=false，admin=true） */
  can_create_subordinate: boolean;
  /** 利润可见性（2026-09-08，后端按 agent_level 派生） */
  profit_visible: boolean;
  created_at: string;
}

/** 下级代理节点（含 hierarchy_level 表示递归层级 0/1/2...） */
export interface SubordinateNode {
  id: number;
  username: string;
  real_name: string;
  agent_level: number;
  parent_id: number | null;
  parent_name: string | null;
  subordinate_count: number;
  status: UserStatus;
  /** 递归层级（0=直接下级，1=下级的下级，...） */
  hierarchy_level: number;
  created_at: string;
}

/** 登录响应 */
export interface LoginResult {
  token: string;
  user: Pick<
    User,
    'id' | 'username' | 'real_name' | 'role' | 'agent_level' | 'profit_visible'
  >;
}

/** JWT Payload */
export interface JwtPayload {
  id: number;
  username: string;
  role: Role;
  iat?: number;
  exp?: number;
}

// ======================== 流量卡相关 ========================

/** 卡类型字典（卡类型管理模块） */
export interface CardTypeInfo {
  id: number;
  /** 类型编码（稳定标识，与 sim_cards.card_type 存储值一致，创建后不可修改） */
  code: string;
  /** 类型名称 */
  name: string;
  status: UserStatus;
  remark?: string;
  /** 关联流量卡数量 */
  card_count?: number;
  /** 关联分账规则数量 */
  rule_count?: number;
  created_at: string;
  updated_at: string;
}

/** 流量卡实体 */
export interface SimCard {
  id: number;
  card_number: string;
  card_type: CardType;
  agent_id: number | null;
  /** JOIN查询补充：代理姓名 */
  agent_name?: string;
  status: CardStatus;
  bound_at: string | null;
  created_at: string;
}

/** 批量绑定请求项 */
export interface BatchBindItem {
  card_number: string;
  card_type: CardType;
  agent_id: number;
}

/** 批量绑定结果 */
export interface BatchBindResult {
  successCount: number;
  errorCount: number;
  errors: string[];
}

// ======================== 实名记录相关 ========================

/** 实名记录实体（列表展示用，已脱敏） */
export interface RealnameRecord {
  id: number;
  card_number: string;
  /** 脱敏后姓名 */
  user_name: string;
  /** 脱敏后身份证号（国际记录为空串） */
  id_card_masked: string;
  /** 记录级证件类型（2026-09-17：本次录入选择的证件类型） */
  identity_type: IdentityType;
  /** 国际记录脱敏护照号（如 E123****7）；国内 null */
  passport_number_masked?: string | null;
  /** 国籍（国际记录，如 CN/US）；国内 null */
  nationality?: string | null;
  agent_id: number;
  agent_name: string;
  audit_status: AuditStatus;
  audit_remark?: string;
  created_at: string;
  updated_at: string;
}

/** 实名记录详情（含完整信息，需权限） */
export interface RealnameRecordDetail extends RealnameRecord {
  /** 完整姓名（有权限时返回） */
  user_name_full?: string;
  /** 完整身份证号（有权限时返回） */
  id_card_full?: string;
  /** 完整护照号（国际记录，owner/admin 可见） */
  passport_number_full?: string | null;
  /** 出生日期（国际记录） */
  date_of_birth?: string | null;
  /** 护照有效期至（国际记录） */
  passport_expiry_date?: string | null;
  /** 身份证正面照片文件路径（国际记录存护照照片页） */
  id_card_front_path?: string;
  /** 身份证反面照片文件路径（国际记录为空） */
  id_card_back_path?: string;
  /** 身份证正面照片访问URL */
  id_card_front_url?: string;
  /** 身份证反面照片访问URL */
  id_card_back_url?: string;
  audited_by?: number;
  audited_at?: string;
}

/** 关联卡号信息（一个身份证号对应多个卡号） */
export interface RelatedCard {
  id: number;
  card_number: string;
  audit_status: AuditStatus;
  created_at: string;
}

/** 按证件号查询关联卡号的入参（2026-09-08：id_type 区分身份证/护照查重路由） */
export interface RelatedCardsQuery {
  /** domestic=按身份证号（默认，兼容旧客户端）；international=按护照号 */
  id_type?: IdentityType;
  /** 身份证号（domestic 路由必填） */
  id_card_number?: string;
  /** 护照号（international 路由必填） */
  passport_number?: string;
}

/** 按证件号查询关联卡号的返回结构 */
export interface RelatedCardsResult {
  total: number;
  user_name: string;
  cards: RelatedCard[];
  id_card_front_path?: string;
  id_card_back_path?: string;
}

/** 创建实名录入 DTO（2026-09-08 扩展：identity_type + 护照字段，全部向后兼容可选） */
export interface CreateRecordDto {
  card_number: string;
  card_type?: string;
  user_name: string;
  /** 身份证号：空 = 暂不上传模式（domestic） */
  id_card_number?: string;
  id_card_front_path?: string;
  id_card_back_path?: string;
  /** 证件类型（2026-09-17：本次录入选择的证件类型，缺省 domestic；旧客户端兼容） */
  identity_type?: IdentityType;
  /** 护照号（international 必填，6-12 位字母数字） */
  passport_number?: string;
  /** 国籍（international 必填，ISO alpha-2 优先） */
  nationality?: string;
  /** 出生日期 YYYY-MM-DD（international 必填） */
  date_of_birth?: string;
  /** 护照有效期至 YYYY-MM-DD（international 必填，>= 提交当天） */
  passport_expiry_date?: string;
  /** 护照照片页路径（international，复用照片上传接口） */
  passport_front_path?: string;
}

/** 批量创建实名录入 DTO（多卡号共享同一证件信息） */
export interface BatchCreateRecordsDto extends Omit<CreateRecordDto, 'card_number'> {
  card_numbers: string[];
}

/** 批量创建实名录入 — 单条结果 */
export interface BatchCreateItemResult {
  card_number: string;
  success: boolean;
  id?: number;
  error?: string;
}

/** 批量创建实名录入 — 返回结构 */
export interface BatchCreateResult {
  successCount: number;
  errorCount: number;
  results: BatchCreateItemResult[];
}

// ======================== 分账相关 ========================

/** 分账规则 */
export interface CommissionRule {
  id: number;
  rule_type: RuleType;
  rule_value: number;
  card_type: CardType | null;
  /** 适用代理等级（1/2/3），null=不限等级（兼容存量规则） */
  agent_level: number | null;
  status: RuleStatus;
  created_at: string;
}

/** 分账明细 */
export interface CommissionRecord {
  id: number;
  card_number: string;
  agent_id: number;
  agent_name: string;
  order_amount: number;
  commission_amount: number;
  rule_id: number | null;
  created_at: string;
}

/** Excel导入异常行 */
export interface ErrorRow {
  row_id: number;
  /** Excel行号 */
  row_index: number;
  card_number: string;
  order_amount: string;
  purchase_time: string;
  error_reason: string;
  is_valid: boolean;
}

/** 导入批次结果 */
export interface ImportResult {
  batchId: number;
  totalRows: number;
  successRows: number;
  errorRows: number;
  errors: ErrorRow[];
}

/** 确认导入结果 */
export interface ConfirmResult {
  batchId: number;
  commissionCount: number;
  totalCommission: number;
}

// ======================== 订单导入（销售系统 25 列订单 Excel） ========================

/** 订单导入错误/警告项（行级） */
export interface OrderImportIssue {
  row: number;
  col: number;
  colLetter: string;
  field: string;
  value: string | null;
  reason: string;
  suggestion: string;
}

/** 订单导入预览（上传后校验结果） */
export interface OrderImportPreview {
  batchId: number;
  sha256: string;
  fileName: string;
  totalRows: number;
  successRows: number;
  errorRows: number;
  warnings: OrderImportIssue[];
  errors: OrderImportIssue[];
  isDuplicate: boolean;
  dupBatchId: number | null;
}

/** 订单导入确认结果 */
export interface OrderImportConfirmResult {
  batchId: number;
  importedCount: number;
  skippedCount: number;
  priceTotalCents: number;
  priceTotalYuan: number;
}

/** 订单导入批次（历史记录） */
export interface OrderImportBatch {
  id: number;
  file_name: string;
  file_sha256: string;
  total_rows: number;
  success_rows: number;
  error_rows: number;
  skipped_rows: number;
  status: 'success' | 'failed' | 'uploaded';
  errors_json: string | null;
  created_at: string;
}

// ======================== 订单分账（以导入订单金额为计算依据） ========================

/** 订单分账明细 */
export interface OrderCommissionRecord {
  id: number;
  order_id: number;
  order_no: string;
  batch_id: number | null;
  card_number: string;
  agent_id: number;
  /** JOIN 补充：代理姓名 */
  agent_name?: string;
  /** 分账计算时使用的代理等级 */
  agent_level: number;
  /** 订单金额（元，来自 orders.price_yuan） */
  order_amount: number;
  commission_amount: number;
  rule_id: number | null;
  created_at: string;
}

/** 订单分账计算结果 */
export interface OrderCommissionCalcResult {
  /** 本次写入分账明细条数 */
  calculatedCount: number;
  /** 计入分账的订单总金额（元） */
  totalOrderAmount: number;
  /** 分账总金额（元） */
  totalCommission: number;
  /** 未匹配到代理（终端号未绑定卡/卡无归属）而跳过的订单数 */
  unmatchedAgentCount: number;
  /** 匹配到代理但无可用规则的订单数（分账金额记 0） */
  noRuleCount: number;
  /** 跳过的已撤销订单数 */
  cancelledSkipped: number;
  /** 卡归属已变更被自动撤销重算的过期分账记录数（归属同步，2026-09-01） */
  staleRefreshedCount: number;
}

/** 订单分账汇总报表 */
export interface OrderCommissionReport {
  totalOrders: number;
  totalAmount: number;
  totalCommission: number;
  byAgent: {
    agent_id: number;
    agent_name: string;
    agent_level: number;
    order_count: number;
    total_amount: number;
    total_commission: number;
  }[];
}

/** 对账报表数据 */
export interface CommissionReport {
  totalOrders: number;
  totalAmount: number;
  totalCommission: number;
  byAgent: {
    agent_id: number;
    agent_name: string;
    order_count: number;
    total_amount: number;
    total_commission: number;
  }[];
}

// ======================== 操作日志 ========================

/** 操作日志 */
export interface OperationLog {
  id: number;
  operator_id: number;
  operator_name: string;
  operation_type: string;
  target_type: string;
  target_id: string;
  change_before: string | null;
  change_after: string | null;
  ip_address: string;
  created_at: string;
}

// ======================== 看板相关 ========================

/** 看板KPI统计 */
export interface DashboardStats {
  todayRecords: number;
  monthSales: number;
  monthCommission: number;
  pendingAudit: number;
}

/** 看板趋势数据 */
export interface DashboardTrends {
  dates: string[];
  records: number[];
  sales: number[];
}

/** 看板图表数据 */
export interface DashboardCharts {
  commissionByAgent: { agent_name: string; commission: number }[];
  auditStatus: { status: string; count: number }[];
  cardTypeDist: { card_type: string; count: number }[];
}

// ======================== OCR 相关 ========================

/** OCR 服务商类型 */
export type OcrProviderType = 'tencent' | 'aliyun';

/** OCR 服务商配置 */
export interface OcrProviderConfig {
  id: number;
  provider: OcrProviderType;
  /** 是否为当前启用的服务商 */
  is_active: boolean;
  /** 腾讯云: SecretId / 阿里云: AccessKeyId */
  secret_id: string;
  /** 腾讯云: SecretKey / 阿里云: AccessKeySecret */
  secret_key: string;
  /** 阿里云: AppCode（阿里云OCR市场服务） */
  app_code?: string;
  /** 地域节点 */
  region?: string;
  /** 备注 */
  remark?: string;
  created_at: string;
  updated_at: string;
}

/** OCR 识别结果（身份证） */
export interface IdCardOcrResult {
  /** 姓名 */
  name?: string;
  /** 身份证号 */
  id_card_number?: string;
  /** 性别 */
  sex?: string;
  /** 民族 */
  nationality?: string;
  /** 出生日期 */
  birth_date?: string;
  /** 地址 */
  address?: string;
  /** 签发机关（反面） */
  authority?: string;
  /** 有效期起始（反面） */
  valid_date_start?: string;
  /** 有效期截止（反面） */
  valid_date_end?: string;
  /** OCR 服务商 */
  provider: OcrProviderType;
  /** 置信度（0-100） */
  confidence?: number;
}

/** OCR 连通性测试结果 */
export interface OcrTestResult {
  success: boolean;
  provider: OcrProviderType;
  message: string;
  latency_ms?: number;
}

/** OCR 调用日志 */
export interface OcrLogEntry {
  id: number;
  provider: OcrProviderType;
  side: 'front' | 'back';
  success: boolean;
  /** 识别出的姓名（成功时） */
  recognized_name?: string;
  /** 识别出的身份证号（成功时） */
  recognized_id_card?: string;
  /** 错误信息（失败时） */
  error_message?: string;
  /** 调用耗时(ms) */
  latency_ms: number;
  /** 调用者 */
  operator_id: number;
  operator_name: string;
  ip_address: string;
  created_at: string;
}

// ======================== 错误码常量 ========================

/** 系统错误码 */
export const ErrorCode = {
  SUCCESS: 0,
  INVALID_PARAMS: 1001,
  UNAUTHORIZED: 1002,
  FORBIDDEN: 1003,
  NOT_FOUND: 1004,
  CONFLICT: 1005,
  VALIDATION_FAILED: 1006,
  FILE_UPLOAD_FAILED: 2001,
  EXCEL_PARSE_FAILED: 2002,
  EXCEL_VALIDATION_FAILED: 2003,
  DATABASE_ERROR: 3001,
  OCR_CONFIG_NOT_FOUND: 4001,
  OCR_CALL_FAILED: 4002,
  OCR_QUOTA_EXCEEDED: 4003,
  OCR_TIMEOUT: 4004,
  INTERNAL_ERROR: 5000,
} as const;

/** 错误码对应的HTTP状态码 */
export const HttpStatus: Record<number, number> = {
  0: 200,
  1001: 400,
  1002: 401,
  1003: 403,
  1004: 404,
  1005: 409,
  1006: 422,
  2001: 422,
  2002: 422,
  2003: 422,
  3001: 500,
  4001: 404,
  4002: 502,
  4003: 429,
  4004: 504,
  5000: 500,
};
